// React
import { useContext, useEffect, useRef } from "react"

// Components
import { ActionButton, ErrorText } from "components"
import { FormSection, Product } from "./components"

// Contexts
import { DataJournalContext } from "contexts"
import { DisabledContext } from "pages/Purchase/Transaction/contexts"
import { DataContext, PaymentCountContext } from "../../contexts"

// Form
import { Controller, useFormContext, useWatch } from "react-hook-form"
import { ErrorMessage } from "@hookform/error-message"

// Third-Party Libraries
import toast from "react-hot-toast"

// Types
import type { ErrorFetch } from "types"
import { deleteAlert, type FormType } from "../../utils"

// Utils
import { convertNumber, useApi, useToggle } from "utils"
import { useApprovalStore } from "pages/Purchase/Transaction/utils"
import { useWarehouseByLocation } from "./components/FormSection/utils/hooks/useWarehouseByLocation"

const getInventoryStatus = (inventory?: boolean | null): boolean => inventory === true

export function Detail(params: {
  index: number
  remove: (key: number) => void
}): JSX.Element {
  // Hooks
  const api = useApi()
  const count = useContext(PaymentCountContext)
  const data = useContext(DataContext)
  const disabled = useContext(DisabledContext)
  const loadingRemove = useToggle(false)
  const { isActive, toggle } = useToggle(false)
  const { tax_code } = useContext(DataJournalContext)
  const { isView } = useApprovalStore()

  // Functions
  const deleteItem = (bill_detail_id: number) => {
    loadingRemove.setActive(true)

    api.delete(
      "/probill/deleteitem",
      { params: { bill_detail_id } }
    ).then(() => {
      params.remove(params.index)
    }).catch((err: ErrorFetch<{ message: string } | null>) => {
      toast.error(err.response.data?.message ?? err.response.message)
    }).finally(() => {
      loadingRemove.setActive(false)
    })
  }

  // Form
  const { control, formState, clearErrors, getValues, setValue } = useFormContext<FormType>()
  const location_id = useWatch({ control, name: "location_id" })
  const detailItem = useWatch({ control, name: `probildetail.${params.index}` })
  const prevLocationId = useRef(location_id)
  const itemInventoryFromPo = data.podetail.find(item => item.product_id === detailItem?.product_id)?.inventory
  const isInventory = getInventoryStatus(detailItem?.inventory ?? itemInventoryFromPo)

  useEffect(() => {
    if (prevLocationId.current !== location_id) {
      prevLocationId.current = location_id
      setValue(`probildetail.${params.index}.location_warehouse_id`, null)
    }
    // eslint-disable-next-line
  }, [location_id])

  useEffect(() => {
    if (!isInventory && detailItem && detailItem.location_warehouse_id !== null) {
      setValue(`probildetail.${params.index}.location_warehouse_id`, null)
    }
    // eslint-disable-next-line
  }, [isInventory, detailItem?.location_warehouse_id])

  useEffect(() => {
    const pid = detailItem?.product_id
    if (!pid) {
      return
    }
    const fromPo = data.podetail.find(p => p.product_id === pid)?.inventory
    if (fromPo !== undefined && fromPo !== null && detailItem?.inventory == null) {
      setValue(`probildetail.${params.index}.inventory`, fromPo)
    }
    // eslint-disable-next-line
  }, [detailItem?.product_id, detailItem?.inventory, data.podetail, params.index])

  if (isActive) {
    const row = getValues(`probildetail.${params.index}`)
    const invFromPo = data.podetail.find(p => p.product_id === row?.product_id)?.inventory
    const detailFormValue = row
      ? { ...row, inventory: row.inventory ?? invFromPo ?? null }
      : row

    return (
      <FormSection
        toggle={toggle}
        location_id={location_id ?? ""}
        isInventory={isInventory}
        value={detailFormValue}
        onSuccess={value => {
          setValue(`probildetail.${params.index}`, {
            ...value,
            inventory: value.inventory ?? invFromPo ?? null
          })
          clearErrors(`probildetail.${params.index}`)
          toggle()
        }}
      />
    )
  }

  return (
    <Controller
      name={`probildetail.${params.index}`}
      control={control}
      render={({ field }) => {
        // Vars
        const tax_name: string | undefined = tax_code.data.find(i => i.value === field.value.tax_id)?.label

        return (
          <WarehouseRow
            field={field}
            params={params}
            formState={formState}
            data={data}
            tax_name={tax_name}
            disabled={disabled}
            isView={isView}
            count={count}
            toggle={toggle}
            deleteItem={deleteItem}
            location_id={location_id ?? ""}
            isInventory={isInventory}
          />
        )
      }}
    />
  )
}

function WarehouseRow(params: {
  field: any
  params: { index: number; remove: (key: number) => void }
  formState: any
  data: any
  tax_name: string | undefined
  disabled: boolean
  isView: boolean
  count: any
  toggle: () => void
  deleteItem: (id: number) => void
  location_id: string
  isInventory: boolean
}): JSX.Element {
  const { field, formState, data, tax_name, disabled, isView, count, toggle, deleteItem, location_id, isInventory } = params
  const { data: warehouseOptions } = useWarehouseByLocation(location_id)
  const warehouse_name = !isInventory
    ? "-"
    : warehouseOptions.find(o => o.value === field.value.location_warehouse_id)?.label ?? field.value.location_warehouse_id ?? "-"

  return (
    <tr>
      {formState.defaultValues?.trx_code && data.approval_status !== 1 ? (
        <></>
      ) : (
        <td>{field.value.qty_order}</td>
      )}

      <td>
        <div>{field.value.quantity_bill}</div>

        <ErrorMessage
          name={`probildetail.${params.params.index}.quantity_bill`}
          errors={formState.errors}
          render={({ message }) => <ErrorText text={message} />}
        />
      </td>

      <Product id={field.value.product_id} />
      <td className="text-right">{convertNumber(field.value.price_bill).intoCurrency}</td>
      <td>{convertNumber(field.value.discount).intoNormalAmount}%</td>
      <td className="text-right">{convertNumber(field.value.total).intoCurrency}</td>
      <td>{field.value.project_name}</td>
      <td>{tax_name}</td>
      <td>
        <div>{warehouse_name}</div>
        <ErrorMessage
          name={`probildetail.${params.params.index}.location_warehouse_id`}
          errors={formState.errors}
          render={({ message }) => <ErrorText text={message} />}
        />
      </td>

      <td>
        {!disabled && !isView && (
          <section className="flex justify-center">
            <ActionButton.Update onClick={toggle} />

            {!count.isLoading && !count.data && (
              <ActionButton.Delete
                className="text-error"
                onClick={() => {
                  const bill_detail_id = field.value.bill_detail_id

                  if (bill_detail_id) {
                    deleteAlert().then(() => deleteItem(bill_detail_id))
                  }

                  params.params.remove(params.params.index)
                }}
              />
            )}
          </section>
        )}
      </td>
    </tr>
  )
}